import Konva from 'konva';

// CleanShot-style path-hugging selection for freehand strokes (pencil /
// highlight). Replaces the AABB transformer with a thin selection stripe drawn
// directly along the source line, slightly wider than the source so a blue
// halo peeks out around the stroke. The stripe is added to `contentLayer` and
// pinned to a zIndex one below the source so it sits beneath the original
// stroke's color, not on top of it.
//
// Freehand strokes don't have meaningful handles to drag (a path is N points,
// not 2 endpoints), so this overlay is read-only. Users can still drag the
// whole stroke to move it because the source node remains `draggable: true`.
export class StrokeSelection {
  constructor(engine) {
    this.engine = engine;
    this.node = null;
    this.stripe = null;
    this._onChange = null;
  }

  attach(node) {
    this.detach();
    this.node = node;

    this.stripe = new Konva.Line({
      points: node.points(),
      x: node.x(),
      y: node.y(),
      scaleX: node.scaleX(),
      scaleY: node.scaleY(),
      rotation: node.rotation(),
      stroke: '#155EEF',
      strokeWidth: this._stripeWidth(),
      lineCap: node.lineCap?.() ?? 'round',
      lineJoin: node.lineJoin?.() ?? 'round',
      tension: node.tension?.() ?? 0,
      opacity: 0.55,
      listening: false,
      perfectDrawEnabled: false,
    });
    this.stripe._isSelectionStripe = true;

    this.engine.contentLayer.add(this.stripe);
    // Slot the stripe immediately below the source so the source's color sits
    // on top of the halo. Konva re-stacks on add, so explicit zIndex.
    this.stripe.zIndex(Math.max(0, node.zIndex()));
    this.engine.contentLayer.batchDraw();

    this._onChange = () => this._sync();
    node.on(
      'xChange.strokesel yChange.strokesel pointsChange.strokesel ' +
      'strokeWidthChange.strokesel scaleXChange.strokesel scaleYChange.strokesel ' +
      'rotationChange.strokesel',
      this._onChange,
    );
  }

  detach() {
    if (!this.node) return;
    this.node.off('.strokesel');
    this.stripe?.destroy();
    this.stripe = null;
    this.node = null;
    this._onChange = null;
    this.engine.contentLayer.batchDraw();
  }

  setVisible(v) {
    this.stripe?.visible(v);
  }

  refresh() {
    this._sync();
  }

  _stripeWidth() {
    const sw = this.node.strokeWidth?.() ?? 4;
    return sw + 6;
  }

  _sync() {
    if (!this.stripe || !this.node) return;
    this.stripe.points(this.node.points());
    this.stripe.position(this.node.position());
    this.stripe.scaleX(this.node.scaleX());
    this.stripe.scaleY(this.node.scaleY());
    this.stripe.rotation(this.node.rotation());
    this.stripe.strokeWidth(this._stripeWidth());
    this.engine.contentLayer.batchDraw();
  }
}
